import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { CalendarToday } from '@material-ui/icons';
import axios from 'axios';
import map from 'lodash/map';
import { BarChart } from '../components/Charts/Bar';
import { CircleDiagram } from '../components/Charts/CirceDiagram';
import { Heatmap } from '../components/Charts/Heatmap';
import { NoInformation} from '../CampaignDetails/Sections/NoInformation';
import {
    ChartWrapper,
    InformationWrapper,
    InformationTitle,
    Numbers,
    InformationDescription
} from '../CampaignDetails/CampaignDetails.styles';
import { useAuth } from '../context/auth';
import { CampaignName as DovePointsTitle } from './Progress/Progress.styles';
import { redirectOutside } from '../utilities';
import { theme } from '../theme';
import { Progress } from './Progress';
import { NoDovePoints } from './NoDovePoints';
import {
    StyledItemIcon,
    Wrapper,
    BuyMore,
    StyledLink,
    FlexWrapper,
    StyledContainer,
    MarginWrapper
} from './Dashboard.styles';
import {
    InformationApiResponse,
    InformationType,
    StatsApiResponse,
    WalletApiResponse,
    WalletType
} from './Dashboard.interface';

export function Dashboard() {
    const { user, authToken, wallet } = useAuth();
    const [adminAccess, setAdminAccess] = useState<boolean>(false);
    const [information, setInformation] = useState<InformationType>();
    const [stats, setStats] = useState<any>();
    const [walletInfo, setWalletInfo] = useState<WalletType>();
    const fetchWallet = () => {
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/payments/clients/campaign-info/`, {headers: { DTOKEN: `${authToken}`}}).then(res => {
            const result = res.data as WalletApiResponse;
            setWalletInfo({
                activeCampaigns: result.active_campaigns,
                userCampaings: result.user_campaigns
            })
        })
    }
    useEffect(() => {
        window.scrollTo(0, 0);
        fetchWallet();
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/statistic/homepage-campaigns/`, {headers: { DTOKEN: `${authToken}`}}).then(res => {
            const result = res.data as InformationApiResponse;
            setInformation({
                dovepoints: {
                    available: result.dovepoints.available,
                    totalBought: result.dovepoints.total_bought
                },
                nextScheduled: result.next_scheduled,
                activeCampaigns: result.active_campaigns,
                totalCampaigns: result.total_campaigns,
                campaignsPerType: result.campaigns_per_type,
            })
        })
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/statistic/homepage/`, {headers: { DTOKEN: `${authToken}`}}).then(res => {
            const result = res.data as StatsApiResponse;
            // TODO: fix interfaces in status and status.campaignsStatistics
            setStats({
                impressionPerLocation: result.impression_per_location,
                impressionPerProduct: result.impression_per_product,
                impressionHeatMap: result.impression_heat_map,
                engagementPerLocation: result.engagement_per_location,
                engagementPerProduct: result.engagement_per_product,
                engagementHeatMap: result.engagement_heat_map,
                userActivity: result.user_activity,
                campaignsStatistics: result.campaigns_statistics.map((campaign): any => {
                    return {
                        title: campaign.title,
                        dovepointsUsed: campaign.dovepoints_used,
                        dovepointsLimit: campaign.dovepoints_limit,
                        id: campaign.id
                    }
                })
            })
        })
        if (user) {
            setAdminAccess(user.isAdmin);
        }
    }, [user])
    const campaignsPerType = {
        title: "Your Camaigns per Type",
        labels: map(information?.campaignsPerType, 'name'),
        datasets: [
            {
                data: map(information?.campaignsPerType, 'value'),
                backgroundColor: [
                    theme.colors.primary,
                    theme.chartsColors.red,
                    theme.chartsColors.orange,
                    theme.chartsColors.mint
                ]
            }
        ]
    };
    const chartCreateObject = (e: {}, color: string) => {
        const data = {
            labels: map(e, 'name'),
            datasets: [
                {
                    data: map(e, 'value'),
                    backgroundColor: color,
                    barThickness: 8
                }
            ]
        }
        return data;
    }
    const userActivityData = {
        title: "User's Activites",
        labels: map(stats?.userActivity, 'name'),
        datasets: [
            {
                data: map(stats?.userActivity, 'value'),
                backgroundColor: [
                    theme.chartsColors.orange,
                    theme.chartsColors.lightRed
                ]
            }
        ]
    }
    return (
        <>
            {!adminAccess && walletInfo && wallet && (!wallet.hasWallet || walletInfo.userCampaings === 0) ? 
                <NoDovePoints />
            : stats && information ? (
                <StyledContainer>
                    <MarginWrapper verified={user ? user.verifiedAccount : false}>
                        <Grid container spacing={2} justify="space-around">
                            <Grid item xs={12} sm={'auto'}>
                                <Wrapper>
                                    {(stats.impressionPerLocation.length > 0 || stats.impressionPerProduct.length > 0 || stats.engagementPerLocation.length > 0 || stats.engagementPerProduct.length > 0)
                                        ?
                                            <>
                                                {stats.campaignsStatistics.length > 0 && <Progress adminAccess={adminAccess} data={stats.campaignsStatistics} />}
                                                {stats.impressionPerLocation.length > 0 && 
                                                    <>
                                                        <ChartWrapper>
                                                            <BarChart data={chartCreateObject(stats.impressionPerLocation, theme.colors.primary)} title="Impressions per location" />
                                                        </ChartWrapper>
                                                        <ChartWrapper>
                                                            <BarChart data={chartCreateObject(stats.impressionPerProduct, theme.chartsColors.lightBlue)} title="Impressions per product" />
                                                        </ChartWrapper>
                                                        <ChartWrapper>
                                                            <InformationDescription><b>Impressions heatmap</b></InformationDescription>
                                                                <Heatmap data={stats.impressionHeatMap} name="impressions" />
                                                        </ChartWrapper>
                                                    </>
                                                }
                                                {stats.engagementPerLocation.length > 0 && 
                                                    <>
                                                        <ChartWrapper>
                                                            <BarChart data={chartCreateObject(stats.engagementPerLocation, theme.chartsColors.orange)} title="Engagements (Clicks) per location" />
                                                        </ChartWrapper>
                                                        <ChartWrapper>
                                                            <BarChart data={chartCreateObject(stats.engagementPerProduct, theme.chartsColors.red)} title="Engagements (Clicks) per product" />
                                                        </ChartWrapper>
                                                        <ChartWrapper>
                                                            <InformationDescription><b>Engagements heatmap</b></InformationDescription>
                                                            <Heatmap data={stats.engagementHeatMap} name="engagements" />
                                                        </ChartWrapper>
                                                    </>
                                                }
                                            </>
                                        : <NoInformation />
                                    }
                                </Wrapper>
                            </Grid>
                            <Grid item xs={12} sm={'auto'}>
                                {!adminAccess &&
                                    <InformationWrapper>
                                        <InformationTitle>DovePoints Available</InformationTitle>
                                        <Numbers>{wallet ? wallet.availablePoints : 0}</Numbers>
                                        <DovePointsTitle>Total bought</DovePointsTitle>
                                        <InformationTitle>{wallet ? wallet.purchasedDovepoints : 0}</InformationTitle>
                                        <BuyMore onClick={() => redirectOutside('/wallet')}>Buy dovepoints</BuyMore>
                                    </InformationWrapper>
                                }
                                {!adminAccess &&
                                    <InformationWrapper>
                                        <FlexWrapper>
                                            <div>
                                                <InformationTitle>Next Scheduled Campaign</InformationTitle>
                                                <Numbers>{information.nextScheduled || '-'}</Numbers>
                                            </div>
                                            <div>
                                                <StyledItemIcon backgroundColor="#2a71d426" iconColor="#2A71D4">
                                                    <CalendarToday />
                                                </StyledItemIcon>
                                            </div>
                                        </FlexWrapper>
                                        <StyledLink to={`${process.env.REACT_APP_INTERNAL_URL}my-campaigns/scheduled`}>Show all</StyledLink>
                                    </InformationWrapper>
                                }
                                <InformationWrapper>
                                    <InformationTitle>Number of Active Campaigns</InformationTitle>
                                    <Numbers>{information.activeCampaigns}</Numbers>
                                    <StyledLink to={`${process.env.REACT_APP_INTERNAL_URL}my-campaigns/active`}>Show all</StyledLink>
                                </InformationWrapper>
                                <InformationWrapper>
                                    <InformationTitle>Total number of Campaigns</InformationTitle>
                                    <Numbers>{information.totalCampaigns}</Numbers>
                                </InformationWrapper>
                                <InformationWrapper>
                                    <CircleDiagram 
                                        diagramData={campaignsPerType.datasets[0].data}
                                        diagramColors={campaignsPerType.datasets[0].backgroundColor}
                                        diagramLabels={campaignsPerType.labels}
                                        showVotes={false} 
                                        title={campaignsPerType.title} 
                                    />
                                </InformationWrapper>
                                {map(stats.userActivity, 'value').reduce((sum, curr) => sum + curr) > 0 &&
                                    <InformationWrapper>
                                        <CircleDiagram 
                                            diagramData={userActivityData.datasets[0].data}
                                            diagramColors={userActivityData.datasets[0].backgroundColor}
                                            diagramLabels={userActivityData.labels}
                                            showVotes={false} 
                                            title={userActivityData.title} 
                                        />
                                    </InformationWrapper>
                                }
                            </Grid>
                        </Grid>
                    </MarginWrapper>
                </StyledContainer>
            ) : <div>Loading</div>
            }
        </>
    )
}
